function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { all, call, delay, put, race, select, getContext, take, takeEvery, takeLatest } from 'redux-saga/effects';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import map from 'lodash/map';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import sortBy from 'lodash/sortBy';
import { v4 as uuidv4 } from 'uuid';
import { LocalError } from 'helpers/errorTypes';
import { decrypt, encrypt, getKeyFromPem, hybridDecrypt, hybridEncrypt } from 'helpers/crypto';
import appInsights from 'helpers/appInsights';
import { getStandards } from 'helpers/standards';
import StorageExchangeTokenService from 'services/StorageExchangeTokenService';
import NotificationsService from 'services/NotificationsService';
import ApiService from 'services/ApiService';
import SecurityService from 'services/SecurityService';
import App from 'modules/App';
import Account from 'modules/Account';
import CloudDrive from 'modules/CloudDrive';
import ContourCloud from 'modules/ContourCloud';
import DataSources from 'modules/DataSources';
import Information from 'modules/Information';
import Notifications from 'modules/Notifications';
import { returnDifferentInHealthData } from './helpers';
import * as actionTypes from './actionTypes';
import * as actions from './actions';
import * as selectors from './selectors';
import messages from './messages';
//----------------------------------------------------------------------------------------------------------------------
function* autoApproveSharingRequest(sharingRequest) {
    if (!sharingRequest) {
        throw new LocalError({
            code: 'NoSharingRequest'
        });
    }
    const patientProfile = yield select(Account.selectors.patientProfile);
    let phiSet = yield select(selectors.phiSet);
    if (!phiSet) {
        const interval = 1000;
        let tries = 10;
        while(!phiSet && tries){
            yield delay(interval);
            phiSet = yield select(selectors.phiSet);
            tries--;
        }
        if (!phiSet) {
            throw new LocalError({
                code: 'NoPhiSet'
            });
        }
    }
    yield put(actions.approveSharingRequest(sharingRequest, patientProfile, phiSet));
    const approveResult = yield take([
        actionTypes.APPROVE_SHARING_REQUEST_SUCCESS,
        actionTypes.APPROVE_SHARING_REQUEST_ERROR
    ]);
    if (approveResult.type === actionTypes.APPROVE_SHARING_REQUEST_ERROR) {
        throw approveResult.error;
    }
    return approveResult.payload.sharingRequest;
}
function* fetchSharingRequests() {
    try {
        const sharingRequests = yield call(ApiService.regionalRequest, '/api/SharingRequest/patient/me');
        const sharingRequestsToBeApproved = filter(sharingRequests, (sr)=>sr.sharingStatus === 'Received' || sr.sharingStatus === 'PendingEnrolling');
        if (sharingRequestsToBeApproved.length) {
            for(let i = 0; i < sharingRequestsToBeApproved.length; i++){
                try {
                    const approvedSharingRequest = yield call(autoApproveSharingRequest, sharingRequestsToBeApproved[i]);
                    const idx = findIndex(sharingRequests, (sr)=>sr.sharingRequestId === approvedSharingRequest.sharingRequestId);
                    if (idx >= 0) {
                        sharingRequests[idx] = approvedSharingRequest;
                    }
                } catch (err) {
                    // Background action
                    if (__DEV__) {
                        console.error(err);
                    } else {
                        appInsights.trackException(err);
                    }
                }
            }
        }
        yield put(actions.fetchSharingRequestsSuccess(sharingRequests));
    } catch (err) {
        yield put(actions.fetchSharingRequestsError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* storeExternalDataSourceToken(payload) {
    yield put(DataSources.actions.storeToken(payload.externalDataSourceId, payload.secret, payload.controlId, payload.scope));
    const fetchVaultResult = yield take([
        DataSources.actionTypes.STORE_TOKEN_SUCCESS,
        DataSources.actionTypes.STORE_TOKEN_ERROR
    ]);
    if (fetchVaultResult.type === DataSources.actionTypes.STORE_TOKEN_ERROR) {
        throw fetchVaultResult.error;
    }
    return fetchVaultResult;
}
function* enrollToClinic({ payload }) {
    let sharingRequest;
    try {
        const { enrollCode, invitationCode } = payload;
        const { email, firstName, lastName, dateOfBirth } = yield select(Information.selectors.information);
        const patientData = {
            firstName,
            lastName,
            dateOfBirth
        };
        const requestUrl = '/api/SharingRequest/patient/enroll';
        sharingRequest = yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            body: {
                enrollCode,
                invitationCode,
                patientData,
                patientEmailAddress: email
            }
        });
        yield call(autoApproveSharingRequest, sharingRequest);
        yield put(actions.enrollInClinicSuccess());
    } catch (err) {
        yield put(actions.enrollInClinicError(err, sharingRequest));
        yield call(App.dispatchError, err, messages);
    }
}
function* approveSharingRequest({ payload }) {
    const { sharingRequest, patientProfile, phiSet } = payload;
    try {
        const { sharingRequestId, clinic } = sharingRequest;
        const { encryptedPhiSetReferenceKey, encryptedExchangeToken } = patientProfile;
        let { phiSetReferenceKey } = patientProfile;
        const keyPair = yield select(Account.selectors.keyPair);
        const passphrase = yield select(Account.selectors.passphrase);
        const pubKeyObj = getKeyFromPem(clinic.publicKey);
        const pubKeyObjSelf = getKeyFromPem(keyPair.pubKeyPem);
        const prvKeyObj = getKeyFromPem(keyPair.prvKeyPem, passphrase);
        if (!phiSetReferenceKey) {
            phiSetReferenceKey = yield call(decrypt, encryptedPhiSetReferenceKey, prvKeyObj);
        }
        const patientExchangeToken = yield call(hybridDecrypt, encryptedExchangeToken, prvKeyObj);
        const { exchangeToken } = yield call(StorageExchangeTokenService.assignExchangeToken, patientExchangeToken, 'SharingRequest', sharingRequestId);
        const externalDataSources = yield select(DataSources.selectors.dataSources());
        const payloadToStoreTokens = [];
        const ccDataSource = externalDataSources.find((item)=>item.dataSourceProvider === DataSources.constants.DATA_SOURCES_TYPE_NAME.CONTOUR_CLOUD);
        if (ccDataSource) {
            payloadToStoreTokens.push({
                externalDataSourceId: parseInt(ccDataSource.externalDataSourceId, 10),
                dataSourceProvider: DataSources.constants.DATA_SOURCES_TYPE_NAME.CONTOUR_CLOUD,
                secret: null,
                controlId: sharingRequest.sharingRequestId
            });
        }
        const callsStoreExternalDataSourceToken = yield payloadToStoreTokens.map((payloadToStoreToken)=>call(storeExternalDataSourceToken, payloadToStoreToken));
        const storeTokensResults = yield all(callsStoreExternalDataSourceToken);
        const callsTokensToEncrypt = storeTokensResults.map((result)=>call(hybridEncrypt, result.payload.response.dataSourceExchangeToken, pubKeyObj));
        const callsTokensToEncryptSelf = storeTokensResults.map((result)=>call(hybridEncrypt, result.payload.response.dataSourceExchangeToken, pubKeyObjSelf));
        const [encryptedPatientPhiSetReferenceKey, encryptedPatientExchangeToken, encryptedPatientExchangeTokenSelf, encryptedDataSourceExchangeTokens, encryptedDataSourceExchangeTokensSelf] = yield all([
            call(encrypt, phiSetReferenceKey, pubKeyObj),
            call(hybridEncrypt, exchangeToken, pubKeyObj),
            call(hybridEncrypt, exchangeToken, pubKeyObjSelf),
            all(callsTokensToEncrypt),
            all(callsTokensToEncryptSelf)
        ]);
        const externalDataSourcesPayload = payloadToStoreTokens.map((item, index)=>({
                externalDataSourceId: item.externalDataSourceId,
                encryptedDataSourceExchangeToken: encryptedDataSourceExchangeTokens[index],
                encryptedDataSourceExchangeTokenSelf: encryptedDataSourceExchangeTokensSelf[index]
            }));
        const requestUrl = `/api/SharingRequest/patient/${sharingRequestId}/approve`;
        const { encryptedStatisticalPersonalityId } = phiSet;
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'PUT',
            body: {
                encryptedPatientPhiSetReferenceKey,
                encryptedPatientExchangeToken,
                encryptedPatientExchangeTokenSelf,
                externalDataSources: externalDataSourcesPayload,
                pwdStatisticalPersonalityId: decrypt(encryptedStatisticalPersonalityId, prvKeyObj)
            }
        });
        const approvedSharingRequest = _object_spread_props(_object_spread({}, sharingRequest), {
            encryptedPatientExchangeTokenSelf,
            sharingStatus: sharingRequest.sharingStatus === 'PendingEnrolling' ? 'Enrolling' : 'Approved'
        });
        yield put(actions.approveSharingRequestSuccess(approvedSharingRequest));
    } catch (err) {
        yield put(actions.approveSharingRequestError(err, sharingRequest));
        yield call(App.dispatchError, err, messages);
    } finally{
        yield put(CloudDrive.actions.clearAuthorizationCode());
    }
}
function* revokeSharingRequest({ payload }) {
    try {
        const { sharingRequest } = payload;
        const requestUrl = `/api/SharingRequest/patient/${sharingRequest.sharingRequestId}/revoke`;
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'PUT'
        });
        const revokedSharingRequest = _object_spread_props(_object_spread({}, sharingRequest), {
            sharingStatus: 'Revoked'
        });
        yield put(actions.revokeSharingRequestSuccess(revokedSharingRequest));
    } catch (err) {
        yield put(actions.revokeSharingRequestError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* makeSendFamilyLinkRequest(invitee) {
    const invitationCode = uuidv4();
    const requestUrl = '/api/FamilyLink';
    yield call(ApiService.regionalRequest, requestUrl, {
        method: 'POST',
        body: _object_spread({
            invitationCode
        }, invitee)
    });
}
function* inviteFamilyMembers({ payload }) {
    try {
        const { invitees } = payload;
        yield all(map(invitees, (invitee)=>call(makeSendFamilyLinkRequest, invitee)));
        yield put(actions.fetchFamilyLinkRequests());
        yield put(actions.inviteFamilyMembersSuccess());
    } catch (err) {
        yield put(actions.inviteFamilyMembersError(err));
        yield call(App.dispatchError, err, messages);
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* makeFetchFamilyLinkInvitationRequestorPublicKey(familyLinkInvitation) {
    const { requestor } = familyLinkInvitation;
    const fetchProfilePublicKey = requestor.profileType === 'PWD' ? SecurityService.fetchPatientProfilePublicKey : SecurityService.fetchCgProfilePublicKey;
    return yield call(fetchProfilePublicKey, requestor.profileId);
}
function* getFamilyLinkInvitationApproveData(familyLinkInvitation) {
    const { familyLinkId } = familyLinkInvitation;
    const patientProfile = yield select(Account.selectors.patientProfile);
    if (!patientProfile) {
        throw new LocalError({
            code: 'PatientProfileNotFound'
        });
    }
    const keyPair = yield select(Account.selectors.keyPair);
    const passphrase = yield select(Account.selectors.passphrase);
    const prvKeyObj = getKeyFromPem(keyPair.prvKeyPem, passphrase);
    const { encryptedPhiSetReferenceKey: patientEncryptedPhiSetReferenceKey, encryptedExchangeToken: patientEncryptedExchangeToken } = patientProfile;
    const patientExchangeToken = yield call(hybridDecrypt, patientEncryptedExchangeToken, prvKeyObj);
    const { exchangeToken } = yield call(StorageExchangeTokenService.assignExchangeToken, patientExchangeToken, 'FamilySharing', familyLinkId);
    let { phiSetReferenceKey } = patientProfile;
    if (!phiSetReferenceKey) {
        phiSetReferenceKey = yield call(decrypt, patientEncryptedPhiSetReferenceKey, prvKeyObj);
    }
    return {
        exchangeToken,
        phiSetReferenceKey
    };
}
function* makeSendApproveFamilyLinkInvitation(familyLinkInvitation, encryptedExchangeToken, encryptedPhiSetReferenceKey) {
    const { invitationCode } = familyLinkInvitation;
    const requestUrl = `/api/FamilyLink/${invitationCode}/approve`;
    return yield call(ApiService.regionalRequest, requestUrl, {
        method: 'PUT',
        body: {
            encryptedExchangeToken,
            encryptedPhiSetReferenceKey
        }
    });
}
function* approveFamilyLinkInvitation(familyLinkInvitation) {
    if (!familyLinkInvitation) {
        throw new LocalError({
            code: 'NoFamilyLinkInvitation'
        });
    }
    const { requestor } = familyLinkInvitation;
    if (!includes([
        'PWD',
        'CG'
    ], requestor.profileType)) {
        throw new LocalError({
            code: 'InvalidFamilyLinkInvitationRequestorProfileType'
        });
    }
    const { exchangeToken, phiSetReferenceKey } = yield call(getFamilyLinkInvitationApproveData, familyLinkInvitation);
    const requestorPublicKey = yield call(makeFetchFamilyLinkInvitationRequestorPublicKey, familyLinkInvitation);
    const requestorPubKeyObj = getKeyFromPem(requestorPublicKey);
    const encryptedExchangeToken = yield call(hybridEncrypt, exchangeToken, requestorPubKeyObj);
    const encryptedPhiSetReferenceKey = yield call(encrypt, phiSetReferenceKey, requestorPubKeyObj);
    return yield call(makeSendApproveFamilyLinkInvitation, familyLinkInvitation, encryptedExchangeToken, encryptedPhiSetReferenceKey);
}
function* makeFetchFamilyLinkInvitations() {
    const requestUrl = '/api/FamilyLink/invitations';
    const familyLinks = yield call(ApiService.regionalRequest, requestUrl);
    const familyLinkToBeApproved = find(familyLinks, {
        status: 'Received'
    });
    if (familyLinkToBeApproved) {
        const approvedFamilyLink = yield call(approveFamilyLinkInvitation, familyLinkToBeApproved);
        const { familyLinkId } = approvedFamilyLink;
        const idx = findIndex(familyLinks, {
            familyLinkId
        });
        familyLinks[idx] = approvedFamilyLink;
    }
    return familyLinks;
}
function* makeFetchFamilyLinkRequests() {
    const requestUrl = '/api/FamilyLink/requests';
    const familyLinks = yield call(ApiService.regionalRequest, requestUrl);
    return sortBy(familyLinks, (fl)=>{
        const isInactive = fl.encryptedExchangeToken && fl.status === 'Approved' ? 0 : 1;
        return `${isInactive} ${fl.invitee.lastName} ${fl.invitee.firstName}`;
    });
}
function* fetchFamilyLinks() {
    try {
        const [familyLinkInvitations, familyLinkRequests] = yield all([
            call(makeFetchFamilyLinkInvitations),
            call(makeFetchFamilyLinkRequests)
        ]);
        yield put(actions.fetchFamilyLinksSuccess(familyLinkInvitations, familyLinkRequests));
    } catch (err) {
        yield put(actions.fetchFamilyLinksError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* fetchFamilyLinkRequests() {
    try {
        const familyLinkRequests = yield call(makeFetchFamilyLinkRequests);
        yield put(actions.fetchFamilyLinkRequestsSuccess(familyLinkRequests));
    } catch (err) {
        yield put(actions.fetchFamilyLinkRequestsError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* revokeFamilyAccess({ payload }) {
    try {
        const { familyLink } = payload;
        const { invitationCode } = familyLink;
        const requestUrl = `/api/FamilyLink/${invitationCode}/revoke`;
        yield call(ApiService.regionalRequest, requestUrl, {
            method: 'PUT'
        });
        yield put(actions.revokeFamilyAccessSuccess(familyLink));
    } catch (err) {
        yield put(actions.revokeFamilyAccessError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* sync({ payload }) {
    try {
        const { patientProfile, passphrase } = payload;
        const accessToken = get(patientProfile, 'accessToken');
        if (accessToken) {
            const account = yield select(Account.selectors.account);
            const accountCCAccessToken = yield select(Account.selectors.accountCCAccessToken);
            const calls = [
                put(CloudDrive.actions.fetchPhiSet(patientProfile, actions.setPhiSet))
            ];
            const takes = [
                take([
                    CloudDrive.actionTypes.FETCH_PHI_SET_SUCCESS,
                    CloudDrive.actionTypes.FETCH_PHI_SET_ERROR
                ])
            ];
            if (!accountCCAccessToken) {
                calls.push(put(Account.actions.getCCAccessToken({
                    account,
                    passphrase
                })));
                takes.push(take([
                    Account.actionTypes.GET_CC_ACCESS_TOKEN_SUCCESS,
                    Account.actionTypes.GET_CC_ACCESS_TOKEN_ERROR
                ]));
            }
            yield all(calls);
            const results = yield all(takes);
            if (results[0].type === CloudDrive.actionTypes.FETCH_PHI_SET_ERROR) {
                yield put(actions.syncError(CloudDrive.actionTypes.FETCH_PHI_SET_ERROR));
                return;
            }
        } else {
            yield put(actions.syncError(new LocalError({
                code: 'EmptyAccessToken'
            })));
            return;
        }
        yield put(actions.migrationSharingRequest());
        const connectedDataSources = yield select(DataSources.selectors.connectedDataSources);
        const accountCCAccessToken = yield select(Account.selectors.accountCCAccessToken);
        const syncErrors = [];
        const externalDataSourcesIds = [];
        const externalDataSourcesTokens = [];
        // const externalDataSourcesIds = connectedDataSources.map((connectedDataSource) => connectedDataSource.externalDataSourceId);
        if (connectedDataSources) {
            for(let i = 0; i < connectedDataSources.length; i++){
                const externalDataSource = connectedDataSources[i];
                if (externalDataSource.dataSourceProvider === DataSources.constants.DATA_SOURCES_TYPE_NAME.CONTOUR_CLOUD) {
                    externalDataSourcesIds.push(externalDataSource.externalDataSourceId);
                    externalDataSourcesTokens.push(_object_spread_props(_object_spread({}, externalDataSource), {
                        accessToken: accountCCAccessToken && accountCCAccessToken.accessToken
                    }));
                } else {
                    externalDataSourcesIds.push(externalDataSource.externalDataSourceId);
                    externalDataSourcesTokens.push(externalDataSource);
                }
            }
        }
        // const externalDataSourcesTokens = connectedDataSources.map((externalDataSource) => {
        //   if (externalDataSource.dataSourceProvider === DataSources.constants.DATA_SOURCES_TYPE_NAME.CONTOUR_CLOUD) {
        //     return {
        //       ...externalDataSource,
        //       accessToken: accountCCAccessToken && accountCCAccessToken.accessToken,
        //     };
        //   }
        //   return externalDataSource;
        // });
        let phiSet = yield select(selectors.phiSet);
        let phiSetDocumentId = yield select(selectors.phiSetDocumentId);
        const { storageProvider, phiSetReferenceKey } = patientProfile;
        const countrySettings = yield select(App.selectors.countrySettings);
        const standards = getStandards(null, countrySettings);
        const payloadDataSources = {
            externalDataSourcesIds,
            phiSet,
            phiSetDocumentId,
            accessToken,
            storageProvider,
            phiSetReferenceKey,
            activePatient: patientProfile,
            passphrase,
            standards,
            externalDataSourcesTokens,
            successAction: actions.setImportedReadings,
            successRelatedDataAction: actions.setImportedRelatedData
        };
        yield put(DataSources.actions.sync(payloadDataSources));
        yield take([
            DataSources.actionTypes.SYNC_SUCCESS,
            DataSources.actionTypes.SYNC_ERROR
        ]);
        phiSet = yield select(selectors.phiSet);
        phiSetDocumentId = yield select(selectors.phiSetDocumentId);
        yield put(ContourCloud.actions.getMe());
        const contourCloudMeResult = yield take([
            ContourCloud.actionTypes.CONTOUR_CLOUD_ME_SUCCESS,
            ContourCloud.actionTypes.CONTOUR_CLOUD_ME_ERROR
        ]);
        if (contourCloudMeResult.type === ContourCloud.actionTypes.CONTOUR_CLOUD_ME_SUCCESS) {
            const patientCC = yield select(ContourCloud.selectors.profile);
            // const account = yield select(Account.selectors.account);
            const disableCcSync = yield getContext('disableCcSync');
            const differentInHealthData = returnDifferentInHealthData(phiSet, patientCC, !disableCcSync);
            // const differentInProfile = returnDifferentInProfile(patientProfile, patientCC);
            // const differentInAccount = returnDifferentInAccount(account, patientCC);
            const elementsToWait = [];
            if (!isEmpty(differentInHealthData)) {
                yield put(CloudDrive.actions.updatePhiSet(differentInHealthData, phiSet, phiSetDocumentId, {
                    phiSetReferenceKey,
                    storageProvider,
                    accessToken
                }, actions.setPhiSet));
                elementsToWait.push(take([
                    CloudDrive.actionTypes.UPDATE_PHI_SET_SUCCESS,
                    CloudDrive.actionTypes.UPDATE_PHI_SET_ERROR
                ]));
            }
            // if (!isEmpty(differentInProfile)) {
            //   yield put(Account.actions.updatePatientProfile({ ...patientProfile, ...differentInProfile }));
            //   elementsToWait.push(
            //     take([Account.actionTypes.UPDATE_PATIENT_PROFILE_SUCCESS, Account.actionTypes.UPDATE_PATIENT_PROFILE_ERROR])
            //   );
            // }
            // if (!isEmpty(differentInAccount)) {
            //   yield put(Account.actions.updateAccount(differentInAccount));
            //   elementsToWait.push(
            //     take([Account.actionTypes.UPDATE_ACCOUNT_SUCCESS, Account.actionTypes.UPDATE_ACCOUNT_ERROR])
            //   );
            // }
            yield all(elementsToWait);
        }
        if (syncErrors.length) {
            yield put(actions.syncError({
                code: 'UnableToSyncAllDataSources'
            }));
        } else {
            yield put(actions.syncSuccess());
        }
    } catch (err) {
        yield put(actions.syncError(err));
    }
}
//----------------------------------------------------------------------------------------------------------------------
function* storeNote({ payload }) {
    try {
        const { notes, phiSet, phiSetDocumentId, phisetVisitId, patientProfile, sharingRequest, visitMetadata } = payload;
        yield put(CloudDrive.actions.storeNotes(notes, phiSet, phiSetDocumentId, phisetVisitId, patientProfile, actions.setNotes));
        yield take([
            CloudDrive.actionTypes.STORE_NOTES_SUCCESS,
            CloudDrive.actionTypes.STORE_NOTES_ERROR
        ]);
        if (sharingRequest && phisetVisitId) {
            const { firstName: patientFirstName, lastName: patientLastName } = yield select(Information.selectors.information);
            const { sharingRequestId, encryptedLocalPatientProfileId: encryptedClinicPatientProfileId } = sharingRequest;
            const { id: clinicId, name: clinicName } = sharingRequest.clinic;
            const hcpProfileId = get(visitMetadata, 'createdBy.hcpProfileId');
            yield call(NotificationsService.sendNoteWrittenByPwdNotification, {
                clinicId,
                clinicName,
                patientFirstName,
                patientLastName,
                encryptedClinicPatientProfileId,
                sharingRequestId,
                hcpProfileId
            });
        }
        yield put(actions.storeNotesSuccess());
    } catch (err) {
        yield put(actions.storeNotesError(err));
    }
}
//----------------------------------------------------------------------------------------------------------------------
// @TODO: Refactor with Account
function* fetchAccessToken(encryptedExchangeToken, prvKeyObj) {
    const exchangeToken = hybridDecrypt(encryptedExchangeToken, prvKeyObj);
    const accessToken = yield call(StorageExchangeTokenService.fetchAccessToken, exchangeToken);
    return {
        accessToken,
        exchangeToken
    };
}
// @TODO: Refactor with Account
function* exchangeTokenWorker(activeFamilyLink, expiresIn, exchangeToken) {
    if (!expiresIn) {
        return;
    }
    const interval = (expiresIn - expiresIn / 4) * 1000;
    while(true){
        yield delay(interval);
        const accessToken = yield call(StorageExchangeTokenService.fetchAccessToken, exchangeToken);
        yield put(actions.activateFamilyMemberSuccess(_object_spread_props(_object_spread({}, activeFamilyLink), {
            accessToken
        })));
    }
}
function* activateFamilyMember({ payload }) {
    try {
        const { familyLink } = payload;
        const { encryptedExchangeToken, encryptedPhiSetReferenceKey } = familyLink;
        let err;
        if (!encryptedPhiSetReferenceKey) {
            err = new LocalError({
                code: 'NoPhiSetReferenceKey'
            });
        } else if (!encryptedExchangeToken) {
            err = new LocalError({
                code: 'NoStorageRefreshToken'
            });
        }
        if (err) {
            yield put(actions.activateFamilyMemberError(err));
            yield call(App.dispatchError, err, messages);
            return;
        }
        const passphrase = yield select(Account.selectors.passphrase);
        const keyPair = yield select(Account.selectors.keyPair);
        const prvKeyObj = getKeyFromPem(keyPair.prvKeyPem, passphrase);
        const activeFamilyLink = _object_spread({}, pick(familyLink, [
            'familyLinkId',
            'invitationCode',
            'status',
            'storageProvider'
        ]), pick(familyLink.invitee, [
            'firstName',
            'lastName',
            'avatar',
            'dateOfBirth'
        ]));
        activeFamilyLink.phiSetReferenceKey = decrypt(encryptedPhiSetReferenceKey, prvKeyObj);
        const { accessToken, exchangeToken } = yield call(fetchAccessToken, encryptedExchangeToken, prvKeyObj);
        const { expiresIn } = accessToken;
        activeFamilyLink.accessToken = accessToken;
        activeFamilyLink.exchangeToken = exchangeToken;
        activeFamilyLink.dataType = 'BGM';
        yield put(actions.activateFamilyMemberSuccess(activeFamilyLink));
        yield race([
            call(exchangeTokenWorker, activeFamilyLink, expiresIn, exchangeToken),
            take(actionTypes.ACTIVATE_FAMILY_MEMBER),
            take(actionTypes.DEACTIVATE_FAMILY_MEMBER),
            take(Account.actionTypes.SIGN_OUT)
        ]);
    } catch (err) {
        yield put(actions.activateFamilyMemberError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* onStoreReadingsSuccess({ payload }) {
    const { updatedPhiSet, newPhiSetDocumentId, phiSetReferenceKey, readings, importData, importId } = payload;
    yield put(actions.setImportedReadings(updatedPhiSet, newPhiSetDocumentId, phiSetReferenceKey, readings));
    if (importId) {
        const { accessToken } = yield select(Account.selectors.accountCCAccessToken);
        yield put(DataSources.actions.storeReadings({
            importData,
            importId,
            accessToken
        }));
    }
}
// TODO: Remove if confirmed
// function* onStoreMeasurementsSuccess({ payload }) {
//   const { phiSet, phiSetDocumentId } = payload;
//   const oldPhiSet = yield select(selectors.phiSet);
//   yield put(actions.setPhiSet(phiSet, phiSetDocumentId));
//   if (!oldPhiSet || (phiSet.treatmentType !== oldPhiSet.treatmentType || phiSet.diabetesType !== oldPhiSet.diabetesType)) {
//     const { accessToken } = yield select(Account.selectors.accountCCAccessToken);
//     yield put(DataSources.actions.storeHealthData({ ...phiSet, accessToken }));
//   }
// }
function* setDataSourceExchangeToken(payload) {
    yield put(DataSources.actions.setDataSourceExchangeToken(payload.externalDataSourceId, payload.encryptedDataSourceExchangeToken));
    const setDataSourceExchangeTokenResult = yield take([
        DataSources.actionTypes.SET_DATA_SOURCE_EXCHANGE_TOKEN_SUCCESS,
        DataSources.actionTypes.SET_DATA_SOURCE_EXCHANGE_TOKEN_ERROR
    ]);
    if (setDataSourceExchangeTokenResult.type === DataSources.actionTypes.SET_DATA_SOURCE_EXCHANGE_TOKEN_ERROR) {
        throw setDataSourceExchangeTokenResult.error;
    }
    return setDataSourceExchangeTokenResult;
}
function* assignTokenExternalDataSource(payload) {
    yield put(DataSources.actions.assignToken(payload.dataSourceExchangeToken, payload.controlId, payload.scope));
    const assignTokenResult = yield take([
        DataSources.actionTypes.ASSIGN_TOKEN_SUCCESS,
        DataSources.actionTypes.ASSIGN_TOKEN_ERROR
    ]);
    if (assignTokenResult.type === DataSources.actionTypes.SET_DATA_SOURCE_EXCHANGE_TOKEN_ERROR) {
        throw assignTokenResult.error;
    }
    return assignTokenResult;
}
function* setDataSourceExchangeTokensSharingRequest({ sharingRequestId, payload }) {
    try {
        const requestUrl = `/api/SharingRequest/patient/setDataSourceExchangeTokens/${sharingRequestId}`;
        return yield call(ApiService.regionalRequest, requestUrl, {
            method: 'POST',
            body: payload
        });
    } catch (err) {
        return err;
    }
}
function* authCCToken(ccDataSource, { holderUID, holderPassphrase }) {
    const { externalDataSourceId, accountExternalDataSourceId } = ccDataSource;
    const requestUrl = '/api/ExternalDataSources/oauth/token';
    const { dataSourceExchangeToken } = yield call(ApiService.regionalRequest, requestUrl, {
        method: 'POST',
        body: {
            externalDataSourceId,
            scope: 'Self',
            controlId: accountExternalDataSourceId,
            holderEntry: {
                holderUID,
                holderPassphrase
            }
        }
    });
    yield all([
        put(Account.actions.listWalletEntries({
            holderUID,
            holderPassphrase
        })),
        put(DataSources.actions.fetchConnectedDataSources())
    ]);
    yield all([
        take([
            Account.actionTypes.LIST_WALLET_ENTRIES_SUCCESS,
            Account.actionTypes.LIST_WALLET_ENTRIES_ERROR
        ]),
        take([
            DataSources.actionTypes.FETCH_CONNECTED_DATA_SOURCES_SUCCESS,
            DataSources.actionTypes.FETCH_CONNECTED_DATA_SOURCES_ERROR
        ])
    ]);
    return dataSourceExchangeToken;
}
function* reauthContourCloud() {
    try {
        let sharingRequests = yield select(selectors.sharingRequests);
        if (isEmpty(sharingRequests)) {
            yield call(fetchSharingRequests);
            sharingRequests = yield select(selectors.sharingRequests);
        }
        const account = yield select(Account.selectors.account);
        const keyPair = yield select(Account.selectors.keyPair);
        const pubKeyObjSelf = getKeyFromPem(keyPair.pubKeyPem);
        const connectedDataSources = yield select(DataSources.selectors.connectedDataSources);
        const dataSourceContourCloud = connectedDataSources && connectedDataSources.find((dataSource)=>dataSource.dataSourceProvider === 'ContourCloud');
        if (!dataSourceContourCloud) {
            const err = new LocalError({
                code: 'NoContourCloudDataSource'
            });
            yield put(actions.reauthContourCloudError(err));
            return;
        }
        const dataSourceExchangeToken = yield call(authCCToken, dataSourceContourCloud, account);
        const encryptedDataSourceExchangeToken = hybridEncrypt(dataSourceExchangeToken, pubKeyObjSelf);
        yield call(setDataSourceExchangeToken, {
            externalDataSourceId: dataSourceContourCloud.externalDataSourceId,
            encryptedDataSourceExchangeToken
        });
        const approvedSharingRequests = sharingRequests.map((sharingRequest)=>_object_spread_props(_object_spread({}, sharingRequest), {
                pubKeyObj: getKeyFromPem(get(sharingRequest, 'clinic.publicKey'))
            })).filter((sharingRequest)=>sharingRequest.sharingStatus === 'Approved');
        if (!isEmpty(approvedSharingRequests)) {
            const assignTokenResults = [];
            for(let sharingRequestIndex = 0; sharingRequestIndex < approvedSharingRequests.length; sharingRequestIndex++){
                const sharingRequest = approvedSharingRequests[sharingRequestIndex];
                const result = yield call(assignTokenExternalDataSource, {
                    dataSourceExchangeToken,
                    controlId: sharingRequest.sharingRequestId,
                    scope: 'Sharing'
                });
                assignTokenResults.push(_object_spread_props(_object_spread({}, result), {
                    sharingRequestId: sharingRequest.sharingRequestId,
                    externalDataSourceId: dataSourceContourCloud.externalDataSourceId,
                    pubKeyObj: getKeyFromPem(get(sharingRequest, 'clinic.publicKey'))
                }));
            }
            const connectedData = assignTokenResults.map((tokenResult)=>{
                if (tokenResult.type === DataSources.actionTypes.ASSIGN_TOKEN_SUCCESS) {
                    return {
                        externalDataSourceId: tokenResult.externalDataSourceId,
                        sharingRequestId: tokenResult.sharingRequestId,
                        encryptedDataSourceExchangeToken: hybridEncrypt(tokenResult.payload.response.dataSourceExchangeToken, tokenResult.pubKeyObj),
                        encryptedDataSourceExchangeTokenSelf: hybridEncrypt(tokenResult.payload.response.dataSourceExchangeToken, pubKeyObjSelf)
                    };
                }
                return null;
            }).filter((tokenResult)=>tokenResult);
            const callsSetDataSourceExchangeToken = approvedSharingRequests.map((sharingRequest)=>{
                const externalDataSources = connectedData.filter((data)=>data.sharingRequestId === sharingRequest.sharingRequestId);
                if (isEmpty(externalDataSources)) {
                    return null;
                }
                return call(setDataSourceExchangeTokensSharingRequest, {
                    sharingRequestId: sharingRequest.sharingRequestId,
                    payload: {
                        externalDataSources
                    }
                });
            }).filter((callSetDataSourceExchangeToken)=>callSetDataSourceExchangeToken);
            yield all(callsSetDataSourceExchangeToken);
        }
        yield put(actions.reauthContourCloudSuccess());
    } catch (err) {
        yield put(actions.reauthContourCloudError(err));
        yield call(App.dispatchError, err, messages);
    }
}
function* updateSharingRequest({ sharingRequestId, payload }) {
    const requestUrl = `/api/SharingRequest/patient/${sharingRequestId}`;
    yield call(ApiService.regionalRequest, requestUrl, {
        method: 'PUT',
        body: payload
    });
}
function* migrationSharingRequest() {
    try {
        const keyPair = yield select(Account.selectors.keyPair);
        const passphrase = yield select(Account.selectors.passphrase);
        const prvKeyObj = getKeyFromPem(keyPair.prvKeyPem, passphrase);
        const phiSet = yield select(selectors.phiSet);
        const sharingRequests = yield select(selectors.sharingRequests);
        const approvedSharingRequestsStatisticalPersonalityId = sharingRequests.filter((sharingRequest)=>sharingRequest.sharingStatus === 'Approved').filter((sharingRequest)=>isEmpty(sharingRequest.encryptedPwdStatisticalPersonalityId));
        const callsSetDataSourceExchangeToken = approvedSharingRequestsStatisticalPersonalityId.map((sharingRequest)=>call(updateSharingRequest, {
                sharingRequestId: sharingRequest.sharingRequestId,
                payload: {
                    pwdStatisticalPersonalityId: decrypt(phiSet.encryptedStatisticalPersonalityId, prvKeyObj)
                }
            }));
        yield all(callsSetDataSourceExchangeToken);
        yield put(actions.migrationSharingRequestSuccess());
    } catch (err) {
        yield put(actions.migrationSharingRequestError(err));
    }
}
function* initializeMyDoctorsPage() {
    try {
        let patientProfile = yield select(Account.selectors.patientProfile);
        const prvKeyPem = yield select(Account.selectors.prvKeyPem);
        const passphrase = yield select(Account.selectors.passphrase);
        if (prvKeyPem && passphrase) {
            yield put(Account.actions.connectToPatientStorage(patientProfile));
            const connectToStorageResult = yield take([
                Account.actionTypes.CONNECT_TO_PATIENT_STORAGE_SUCCESS,
                Account.actionTypes.CONNECT_TO_PATIENT_STORAGE_ERROR
            ]);
            if (connectToStorageResult.type === Account.actionTypes.CONNECT_TO_PATIENT_STORAGE_ERROR) {
                throw connectToStorageResult.error;
            }
            patientProfile = yield select(Account.selectors.patientProfile);
            yield put(CloudDrive.actions.fetchPhiSet(patientProfile, actions.setPhiSet));
            const fetchPhiSetResult = yield take([
                CloudDrive.actionTypes.FETCH_PHI_SET_SUCCESS,
                CloudDrive.actionTypes.FETCH_PHI_SET_ERROR
            ]);
            if (fetchPhiSetResult.type === CloudDrive.actionTypes.FETCH_PHI_SET_ERROR) {
                throw fetchPhiSetResult.error;
            }
            yield put(actions.fetchSharingRequests());
            const fetchScharingRequestsResult = yield take([
                actionTypes.FETCH_SHARING_REQUESTS_ERROR,
                actionTypes.FETCH_SHARING_REQUESTS_SUCCESS
            ]);
            if (fetchScharingRequestsResult.type === actionTypes.FETCH_SHARING_REQUESTS_ERROR) {
                throw fetchScharingRequestsResult.error;
            }
            const sharingRequest = yield select(selectors.sharingRequests);
            if (!sharingRequest || !sharingRequest.length) {
                const redirect = yield getContext('redirect');
                const getUrl = yield getContext('getUrl');
                redirect(getUrl('mobile-enroll-in-clinic'));
            }
        }
        yield put(actions.initializeMyDoctorsPageSuccess());
    } catch (error) {
        yield put(actions.initializeMyDoctorsPageError(error));
    }
}
function* backFromEnrollInClinic() {
    const sharingRequest = yield select(selectors.sharingRequests);
    const redirect = yield getContext('redirect');
    const getUrl = yield getContext('getUrl');
    let url = null;
    if (!sharingRequest || !sharingRequest.length) {
        url = getUrl('callback-close-webview-mobile');
    } else {
        url = getUrl('mobile-my-doctors');
    }
    yield redirect(url);
}
function* refreshOnNewNotificationAdded({ payload }) {
    const { notification } = payload;
    const { notificationTrigger } = notification;
    const { NOTIFICATIONS_TRIGGERS } = Notifications.constants;
    switch(notificationTrigger){
        case NOTIFICATIONS_TRIGGERS.FAMILY_LINK_INVITATION_ACCEPTED:
        case NOTIFICATIONS_TRIGGERS.FAMILY_LINK_REVOKED:
            {
                yield put(actions.fetchFamilyLinkRequests());
                break;
            }
        default:
    }
}
function* sagas() {
    yield takeLatest(actionTypes.FETCH_SHARING_REQUESTS, fetchSharingRequests);
    yield takeLatest(actionTypes.ENROLL_IN_CLINIC, enrollToClinic);
    yield takeLatest(actionTypes.APPROVE_SHARING_REQUEST, approveSharingRequest);
    yield takeLatest(actionTypes.REVOKE_SHARING_REQUEST, revokeSharingRequest);
    yield takeLatest(actionTypes.INVITE_FAMILY_MEMBERS, inviteFamilyMembers);
    yield takeLatest(actionTypes.FETCH_FAMILY_SHARING_LINKS, fetchFamilyLinks);
    yield takeLatest(actionTypes.FETCH_FAMILY_SHARING_LINK_REQUESTS, fetchFamilyLinkRequests);
    yield takeLatest(actionTypes.REVOKE_FAMILY_ACCESS, revokeFamilyAccess);
    yield takeLatest(actionTypes.ACTIVATE_FAMILY_MEMBER, activateFamilyMember);
    yield takeLatest(actionTypes.SYNC, sync);
    yield takeEvery(actionTypes.STORE_NOTES, storeNote);
    yield takeLatest(actionTypes.ON_STORE_READINGS_SUCCESS, onStoreReadingsSuccess);
    // TODO: Remove if confirmed
    // yield takeLatest(actionTypes.ON_STORE_MEASUREMENT_SUCCESS, onStoreMeasurementsSuccess);
    yield takeLatest(actionTypes.REAUTH_CONTOUR_CLOUD, reauthContourCloud);
    yield takeLatest(actionTypes.MIGRATION_SHARING_REQUEST, migrationSharingRequest);
    yield takeLatest(actionTypes.INITIALIZE_MY_DOCTORS_PAGE, initializeMyDoctorsPage);
    yield takeLatest(actionTypes.BACK_FROM_ENROLL_IN_CLINIC_PAGE, backFromEnrollInClinic);
    yield takeLatest(Notifications.actionTypes.ADD_NEW_NOTIFICATION, refreshOnNewNotificationAdded);
}
export default [
    sagas
];
